// Shared proximity helpers for the Feb 2026 Logistics Router.
// Extracted from AdminRoutesRegions.sortByProximity so Supervisor + Crew
// Leader boards can reuse the same greedy nearest-neighbor logic.

/**
 * Greedy nearest-neighbor path through a list of geocoded properties.
 * Skips items without `latitude` (they're appended at the tail unchanged).
 * Returns a new array — inputs are not mutated.
 */
export function sortPropertiesByProximity(props) {
  const geocoded = (props || []).filter((p) => p && p.latitude != null && p.longitude != null);
  const missing = (props || []).filter((p) => p && (p.latitude == null || p.longitude == null));
  if (geocoded.length < 2) return [...geocoded, ...missing];
  const remaining = [...geocoded];
  const order = [remaining.shift()];
  while (remaining.length) {
    const last = order[order.length - 1];
    let bestIdx = 0;
    let bestD = Infinity;
    remaining.forEach((p, i) => {
      const d = (p.latitude - last.latitude) ** 2 + (p.longitude - last.longitude) ** 2;
      if (d < bestD) { bestD = d; bestIdx = i; }
    });
    order.push(remaining.splice(bestIdx, 1)[0]);
  }
  return [...order, ...missing];
}

/**
 * Compose a proximity sort with per-city preference biasing.
 * `preferredCities` bubble to the top (regardless of geographic distance),
 * then within each bucket the greedy proximity walk kicks in.
 */
export function sortPropertiesByCityAndProximity(props, preferredCities = []) {
  const preferred = new Set((preferredCities || []).map((c) => (c || '').toLowerCase()));
  if (!preferred.size) return sortPropertiesByProximity(props);
  const inPref = [];
  const outPref = [];
  (props || []).forEach((p) => {
    ((p?.city || '').toLowerCase() && preferred.has((p.city || '').toLowerCase()) ? inPref : outPref).push(p);
  });
  return [...sortPropertiesByProximity(inPref), ...sortPropertiesByProximity(outPref)];
}

/**
 * Haversine-lite: convert two lat/lng pairs into a rough "distance" number
 * for display. Not GPS-accurate — just enough to render "0.7 mi" hints on
 * assignment cards. Returns kilometers (multiply by 0.621371 for miles).
 */
export function approxKm(a, b) {
  if (!a || !b || a.latitude == null || b.latitude == null) return null;
  const R = 6371; // km
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const sin1 = Math.sin(dLat / 2);
  const sin2 = Math.sin(dLng / 2);
  const h = sin1 * sin1 + Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * sin2 * sin2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
